import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';

// WhatsApp business data simulation
const businessData = [
    { id: 1, name: 'E-Commerce Store', type: 'Retail', messages: 'Order updates', segment: 'Sales', color: 'bg-green-500', x: 20, y: 15 },
    { id: 2, name: 'Restaurant Chain', type: 'Food', messages: 'Menu & Orders', segment: 'Customer Service', color: 'bg-blue-500', x: 60, y: 25 },
    { id: 3, name: 'Healthcare Clinic', type: 'Medical', messages: 'Appointments', segment: 'Booking', color: 'bg-red-500', x: 80, y: 60 },
    { id: 4, name: 'Tech Startup', type: 'SaaS', messages: 'Support tickets', segment: 'Support', color: 'bg-purple-500', x: 35, y: 70 },
    { id: 5, name: 'Real Estate', type: 'Property', messages: 'Property alerts', segment: 'Marketing', color: 'bg-orange-500', x: 15, y: 45 },
    { id: 6, name: 'Education Hub', type: 'Learning', messages: 'Course updates', segment: 'Engagement', color: 'bg-teal-500', x: 70, y: 35 },
];

const segmentColors = {
    'Sales': 'border-green-500 bg-green-50',
    'Customer Service': 'border-blue-500 bg-blue-50',
    'Booking': 'border-red-500 bg-red-50',
    'Support': 'border-purple-500 bg-purple-50',
    'Marketing': 'border-orange-500 bg-orange-50',
    'Engagement': 'border-teal-500 bg-teal-50',
};

// WhatsApp logo component
const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.746"/>
    </svg>
);

export function WhatsAppBusinessAnimation() {
    const ref = useRef(null);
    const isInView = useInView(ref);
    const [animationStage, setAnimationStage] = useState(0);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [messageCount, setMessageCount] = useState(0);

    useEffect(() => {
        const timeoutIds = [];

        if (isInView) {
            // Stage 1: Show scattered businesses
            timeoutIds.push(setTimeout(() => {
                setAnimationStage(1);
            }, 500));

            // Stage 2: Start connecting to WhatsApp API
            timeoutIds.push(setTimeout(() => {
                setAnimationStage(2);
            }, 2000));

            // Stage 3: Show business segments
            timeoutIds.push(setTimeout(() => {
                setAnimationStage(3);
            }, 3500));

            // Stage 4: Highlight segments cyclically
            timeoutIds.push(setTimeout(() => {
                const segments = ['Sales', 'Customer Service', 'Support', 'Marketing'];
                let currentIndex = 0;

                const highlightInterval = setInterval(() => {
                    setSelectedSegment(segments[currentIndex]);
                    currentIndex = (currentIndex + 1) % segments.length;
                }, 1500);

                timeoutIds.push(highlightInterval);
            }, 4500));

            // Message counter animation
            const messageInterval = setInterval(() => {
                setMessageCount(prev => prev + Math.floor(Math.random() * 5) + 1);
            }, 800);
            timeoutIds.push(messageInterval);

        } else {
            setAnimationStage(0);
            setSelectedSegment(null);
            setMessageCount(0);
        }

        return () => {
            timeoutIds.forEach(id => clearTimeout(id));
        };
    }, [isInView]);

    return (
        <div
            ref={ref}
            className="w-full h-full relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-gray-200"
        >
            {/* Gradient overlays */}
            <div className="pointer-events-none absolute bottom-0 left-0 h-20 w-full bg-gradient-to-t from-white/80 to-transparent z-20"></div>
            <div className="pointer-events-none absolute top-0 left-0 h-20 w-full bg-gradient-to-b from-white/80 to-transparent z-20"></div>

            {/* Main visualization area */}
            <div className="relative w-full h-full max-w-md max-h-80 mx-auto">

                {/* WhatsApp API Center */}
                <motion.div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                        scale: animationStage >= 2 ? 1 : 0,
                        opacity: animationStage >= 2 ? 1 : 0
                    }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    <div className="relative">
                        <div className="size-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                            <WhatsAppIcon className="size-8 text-white" />
                        </div>

                        {/* API text */}
                        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                            Snwolley API
                        </div>

                        {/* Pulsing rings */}
                        <motion.div
                            className="absolute inset-0 rounded-full border-2 border-green-500/30"
                            animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.5, 0, 0.5]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    </div>
                </motion.div>

                {/* Business dots */}
                <AnimatePresence>
                    {businessData.map((business, index) => (
                        <motion.div
                            key={business.id}
                            className="absolute"
                            initial={{
                                left: '50%',
                                top: '50%',
                                x: '-50%',
                                y: '-50%',
                                opacity: 0,
                                scale: 0
                            }}
                            animate={{
                                left: animationStage >= 1 ? `${business.x}%` : '50%',
                                top: animationStage >= 1 ? `${business.y}%` : '50%',
                                opacity: animationStage >= 1 ? 1 : 0,
                                scale: animationStage >= 1 ? 1 : 0,
                            }}
                            transition={{
                                duration: 0.8,
                                delay: index * 0.1,
                                ease: "easeOut"
                            }}
                        >
                            <motion.div
                                className={`relative size-4 ${business.color} rounded-full shadow-md cursor-pointer border-2 border-white`}
                                whileHover={{ scale: 1.2 }}
                                animate={{
                                    scale: selectedSegment === business.segment ? 1.3 : 1,
                                    boxShadow: selectedSegment === business.segment
                                        ? '0 0 20px rgba(34, 197, 94, 0.6)'
                                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Business info tooltip */}
                                <AnimatePresence>
                                    {selectedSegment === business.segment && (
                                        <motion.div
                                            className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-40"
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 5 }}
                                        >
                                            {business.name} - {business.messages}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Message indicators */}
                                <AnimatePresence>
                                    {animationStage >= 2 && (
                                        <motion.div
                                            className="absolute -top-1 -right-1 size-2 bg-red-500 rounded-full border border-white"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: [0, 1.2, 1] }}
                                            transition={{ 
                                                duration: 0.5, 
                                                delay: Math.random() * 2,
                                                repeat: Infinity,
                                                repeatDelay: Math.random() * 3 + 2
                                            }}
                                        />
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* Business Use Case Clusters */}
                <AnimatePresence>
                    {animationStage >= 3 && (
                        <>
                            {/* Sales Segment */}
                            <motion.div
                                className={`absolute top-[10%] left-[10%] w-24 h-20 rounded-xl border-2 ${segmentColors.Sales} ${selectedSegment === 'Sales' ? 'scale-110 shadow-lg' : ''}`}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 0.7, scale: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="p-2 text-center">
                                    <div className="text-xs font-semibold text-green-700">Sales</div>
                                    <div className="text-xs text-green-600">Order Updates</div>
                                </div>
                            </motion.div>

                            {/* Customer Service Segment */}
                            <motion.div
                                className={`absolute top-[15%] right-[15%] w-24 h-20 rounded-xl border-2 ${segmentColors['Customer Service']} ${selectedSegment === 'Customer Service' ? 'scale-110 shadow-lg' : ''}`}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 0.7, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <div className="p-2 text-center">
                                    <div className="text-xs font-semibold text-blue-700">Service</div>
                                    <div className="text-xs text-blue-600">Customer Care</div>
                                </div>
                            </motion.div>

                            {/* Support Segment */}
                            <motion.div
                                className={`absolute bottom-[15%] right-[10%] w-24 h-20 rounded-xl border-2 ${segmentColors.Support} ${selectedSegment === 'Support' ? 'scale-110 shadow-lg' : ''}`}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 0.7, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="p-2 text-center">
                                    <div className="text-xs font-semibold text-purple-700">Support</div>
                                    <div className="text-xs text-purple-600">Help Desk</div>
                                </div>
                            </motion.div>

                            {/* Marketing Segment */}
                            <motion.div
                                className={`absolute bottom-[20%] left-[15%] w-24 h-20 rounded-xl border-2 ${segmentColors.Marketing} ${selectedSegment === 'Marketing' ? 'scale-110 shadow-lg' : ''}`}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 0.7, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <div className="p-2 text-center">
                                    <div className="text-xs font-semibold text-orange-700">Marketing</div>
                                    <div className="text-xs text-orange-600">Campaigns</div>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Connection lines from businesses to WhatsApp API */}
                <AnimatePresence>
                    {animationStage >= 2 && businessData.map((business, index) => (
                        <motion.svg
                            key={`line-${business.id}`}
                            className="absolute inset-0 w-full h-full pointer-events-none z-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.3 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <motion.line
                                x1={`${business.x}%`}
                                y1={`${business.y}%`}
                                x2="50%"
                                y2="50%"
                                stroke="rgb(34, 197, 94)"
                                strokeWidth="1"
                                strokeDasharray="3,3"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 1, delay: index * 0.1 }}
                            />
                        </motion.svg>
                    ))}
                </AnimatePresence>

                {/* Floating message bubbles */}
                <AnimatePresence>
                    {animationStage >= 2 && (
                        <>
                            <motion.div
                                className="absolute top-[30%] left-[5%] bg-white rounded-lg shadow-md p-2 text-xs max-w-20 border border-gray-200"
                                initial={{ opacity: 0, scale: 0, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 1 }}
                            >
                                <div className="text-gray-600">Your order #1234 is ready! 📦</div>
                            </motion.div>

                            <motion.div
                                className="absolute top-[60%] right-[5%] bg-green-500 text-white rounded-lg shadow-md p-2 text-xs max-w-20"
                                initial={{ opacity: 0, scale: 0, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 1.5 }}
                            >
                                <div>Table reserved for 7PM ✅</div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            {/* Bottom insights panel */}
            <AnimatePresence>
                {animationStage >= 3 && (
                    <motion.div
                        className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg p-3 z-30"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-xs font-medium">Snwolley Business API Active</span>
                            </div>
                            <div className="text-xs text-gray-600">
                                {messageCount.toLocaleString()}+ messages sent
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
